/**
 * 프리뷰 패널 안에서 부분 문자열 검색 — 대소문자 무시 (한글은 동일).
 *
 * - setQuery(q): 모든 매치를 <mark class="search-hit"> 로 감싸고 첫 매치로 점프.
 * - next() / prev(): 현재 하이라이트(.is-current) 이동 + scrollIntoView.
 * - clear(): 모든 mark 제거.
 * - reapply(): 프리뷰가 다시 렌더링된 직후 같은 쿼리로 다시 하이라이트.
 *
 * 텍스트 노드 walker가 <script>, <style>, 이미 있는 <mark.search-hit> 안은 건너뜀.
 * Mermaid SVG 내부 <text> 같은 노드도 매치 대상에 포함됨 (SVG 안 텍스트도 찾고 싶을 수 있음).
 */
export interface SearchStatus {
  query: string;
  total: number;
  current: number; // 1-based, 매치 없을 땐 0
}

export interface SearchHandle {
  setQuery(q: string): SearchStatus;
  next(): SearchStatus;
  prev(): SearchStatus;
  reapply(): SearchStatus;
  clear(): void;
  getStatus(): SearchStatus;
  onStatusChange(cb: (s: SearchStatus) => void): void;
}

const HIT_CLASS = 'search-hit';
const CURRENT_CLASS = 'is-current';
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT']);

export function attachSearch(root: HTMLElement): SearchHandle {
  let query = '';
  let hits: HTMLElement[] = [];
  let cursor = -1; // hits index, -1이면 매치 없음
  const listeners: Array<(s: SearchStatus) => void> = [];

  function status(): SearchStatus {
    return {
      query,
      total: hits.length,
      current: cursor >= 0 ? cursor + 1 : 0,
    };
  }
  function emit(): SearchStatus {
    const s = status();
    for (const l of listeners) l(s);
    return s;
  }

  /** 모든 <mark.search-hit> 를 풀어서 원본 텍스트 노드로 복원. */
  function unmark(): void {
    const marks = root.querySelectorAll<HTMLElement>(`mark.${HIT_CLASS}`);
    marks.forEach((m) => {
      const parent = m.parentNode;
      if (!parent) return;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
    });
    // 인접 텍스트 노드 병합 — 다음 검색 시 substring이 노드 경계로 끊기지 않게.
    root.normalize();
    hits = [];
    cursor = -1;
  }

  /** root 안 텍스트 노드를 순회하며 매치를 <mark>로 감싼다. */
  function highlight(q: string): void {
    if (!q) return;
    const ql = q.toLowerCase();
    const collected: Text[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node: Node) {
        const t = node as Text;
        if (!t.nodeValue || t.nodeValue.length === 0) return NodeFilter.FILTER_REJECT;
        // 부모가 skip 대상이면 건너뜀.
        let p: Node | null = t.parentNode;
        while (p && p !== root) {
          if (p.nodeType === Node.ELEMENT_NODE) {
            const el = p as Element;
            // SVG/MathML 안 <style>/<script>는 tagName이 소문자라 normalize.
            if (SKIP_TAGS.has(el.tagName.toUpperCase())) return NodeFilter.FILTER_REJECT;
            if (el.tagName === 'MARK' && el.classList.contains(HIT_CLASS)) {
              return NodeFilter.FILTER_REJECT;
            }
          }
          p = p.parentNode;
        }
        return t.nodeValue.toLowerCase().includes(ql)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    let n: Node | null;
    while ((n = walker.nextNode())) collected.push(n as Text);

    for (const text of collected) {
      const value = text.nodeValue ?? '';
      const lower = value.toLowerCase();
      let start = 0;
      let idx = lower.indexOf(ql, start);
      if (idx < 0) continue;

      const frag = document.createDocumentFragment();
      while (idx >= 0) {
        if (idx > start) frag.appendChild(document.createTextNode(value.slice(start, idx)));
        const mark = document.createElement('mark');
        mark.className = HIT_CLASS;
        mark.appendChild(document.createTextNode(value.slice(idx, idx + q.length)));
        frag.appendChild(mark);
        hits.push(mark);
        start = idx + q.length;
        idx = lower.indexOf(ql, start);
      }
      if (start < value.length) frag.appendChild(document.createTextNode(value.slice(start)));
      text.parentNode?.replaceChild(frag, text);
    }
  }

  function setCurrent(i: number): void {
    if (cursor >= 0 && hits[cursor]) hits[cursor].classList.remove(CURRENT_CLASS);
    cursor = hits.length === 0 ? -1 : ((i % hits.length) + hits.length) % hits.length;
    if (cursor >= 0 && hits[cursor]) {
      hits[cursor].classList.add(CURRENT_CLASS);
      hits[cursor].scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  return {
    setQuery(q: string): SearchStatus {
      unmark();
      query = q;
      if (q.length > 0) {
        highlight(q);
        setCurrent(0);
      }
      return emit();
    },
    next(): SearchStatus {
      if (hits.length === 0) return emit();
      setCurrent(cursor + 1);
      return emit();
    },
    prev(): SearchStatus {
      if (hits.length === 0) return emit();
      setCurrent(cursor - 1);
      return emit();
    },
    reapply(): SearchStatus {
      // 프리뷰가 새로 렌더된 직후 호출. unmark는 이미 의미 없음(새 DOM).
      hits = [];
      cursor = -1;
      if (query) {
        highlight(query);
        setCurrent(0);
      }
      return emit();
    },
    clear(): void {
      query = '';
      unmark();
      emit();
    },
    getStatus(): SearchStatus {
      return status();
    },
    onStatusChange(cb: (s: SearchStatus) => void): void {
      listeners.push(cb);
    },
  };
}
